import { useState, useEffect } from 'react';
import { Message } from '../../types/community';
import { Pin, Users, Trash2, X } from 'lucide-react';
import { formatTimeShort } from '../../utils/formatters';
import { motion } from 'framer-motion';
import { VideoEmbed } from './VideoEmbed';
import ReactMarkdown from 'react-markdown';
import { Portal } from '../common/Portal';

interface PinnedMessagesProps {
  messages: Message[];
  isAdmin: boolean;
  currentUserId?: string;
  onTogglePin: (messageId: string, isPinned: boolean) => void;
  onDelete: (messageId: string, userId: string) => void;
  onToggleLike: (messageId: string) => void;
  onViewThread?: (messageId: string) => void;
}

export function PinnedMessages({
  messages,
  isAdmin,
  currentUserId,
  onTogglePin,
  onDelete,
  onToggleLike: _onToggleLike,
  onViewThread,
}: PinnedMessagesProps) {
  // State to track video URLs for each pinned message
  const [videoUrls, setVideoUrls] = useState<Record<string, string | null>>({});
  // Track which message's image is open in a modal
  const [openImageFor, setOpenImageFor] = useState<string | null>(null);
  // Track intrinsic dimensions per message image
  const [imgDims, setImgDims] = useState<Record<string, { w: number; h: number } | null>>({});

  // Compute modal sizing based on current image aspect ratio
  const currentDims = openImageFor ? imgDims[openImageFor] : null;
  const aspect = currentDims ? currentDims.w / currentDims.h : null;
  const modalSizeClass = aspect == null
    ? 'max-w-[85vw] max-h-[80vh]'
    : aspect > 1.2
      ? 'max-w-[92vw] max-h-[70vh]'
      : aspect < 0.8
        ? 'max-w-[65vw] max-h-[90vh]'
        : 'max-w-[80vw] max-h-[80vh]';

  // Function to detect video URLs in message content
  const detectVideoUrl = (content: string) => {
    // YouTube regex patterns
    const youtubeRegex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})(?:[^\s]*)/;
    // Loom regex patterns
    const loomRegex = /(?:https?:\/\/)?(?:www\.)?loom\.com\/(?:share|embed)\/([a-zA-Z0-9]+)(?:[^\s]*)/;
    
    // Check for YouTube links
    const youtubeMatch = content.match(youtubeRegex);
    if (youtubeMatch && youtubeMatch[0]) {
      return youtubeMatch[0];
    }
    
    // Check for Loom links
    const loomMatch = content.match(loomRegex);
    if (loomMatch && loomMatch[0]) {
      return loomMatch[0];
    }
    
    return null;
  };
  
  // Function to remove video URLs from the displayed message content
  const getFilteredContent = (content: string, videoUrl: string | null): string => {
    if (!videoUrl) return content;
    
    // Remove the video URL from the content
    return content.replace(videoUrl, '').trim();
  };

  // Detect videos in pinned messages when component mounts or messages change
  useEffect(() => {
    const newVideoUrls: Record<string, string | null> = {};
    
    messages.forEach(message => {
      const videoUrl = detectVideoUrl(message.content);
      if (videoUrl) {
        newVideoUrls[message.id] = videoUrl;
      }
    });
    
    setVideoUrls(newVideoUrls);
  }, [messages]);

  // Close image modal with ESC
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenImageFor(null);
    };
    if (openImageFor) document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [openImageFor]);
  
  if (messages.length === 0) return null;

  return (
    <div className="mb-8">
      <motion.div 
        className="flex items-center gap-2 px-4 py-3 mb-4"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div className="relative">
          <Pin className="w-5 h-5 text-[#c9fffc] relative z-10" />
          <div className="absolute inset-0 bg-[#c9fffc]/30 rounded-full blur-sm"></div>
        </div>
        <h3 className="text-lg font-medium text-white">Important Announcements</h3>
        <div className="h-[1px] flex-grow bg-gradient-to-r from-[#c9fffc]/50 via-[#c9fffc]/10 to-transparent"></div>
      </motion.div>
      
      <motion.div
        className="bg-[#151D29]/30 backdrop-blur-md rounded-xl shadow-lg relative overflow-hidden border border-[#c9fffc]/30"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        whileHover={{ boxShadow: '0 0 20px 0 rgba(201, 255, 252, 0.2)' }}
      >
        {/* Glowing effects */}
        <div className="absolute -top-20 -right-20 w-40 h-40 bg-[#c9fffc]/10 rounded-full blur-xl opacity-80"></div>
        <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-[#c9fffc]/5 rounded-full blur-xl opacity-50"></div>
        
        {/* Single header for all pinned messages */}
        <div className="flex items-center justify-between bg-gradient-to-r from-[#c9fffc]/30 to-[#c9fffc]/10 backdrop-blur-md px-5 py-3 relative overflow-hidden">
          <div className="absolute inset-0 bg-[#151D29]/10 backdrop-blur-md"></div>
          <div className="flex items-center gap-2 relative z-10">
            <Pin className="w-5 h-5 text-[#c9fffc]" />
            <span className="text-sm font-semibold text-[#c9fffc]">
              Important Announcements
            </span>
          </div>
          <div className="relative z-10 flex items-center gap-2">
            <div className="px-2 py-0.5 rounded-full bg-[#c9fffc]/20 border border-[#c9fffc]/30 text-xs text-[#c9fffc]">
              {messages.length} {messages.length === 1 ? 'Message' : 'Messages'}
            </div>
          </div>
        </div>
        
        <div className="divide-y divide-[#c9fffc]/10">
          {messages.map((message) => (
            <div
              key={message.id}
              className="group px-5 pb-5 pt-4 relative z-10 cursor-pointer"
              onClick={() => onViewThread && onViewThread(message.id)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if ((e.key === 'Enter' || e.key === ' ') && e.currentTarget === e.target) {
                  e.preventDefault();
                  onViewThread && onViewThread(message.id);
                }
              }}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-full overflow-hidden bg-[#151D29]/50 border border-[#c9fffc]/20 flex-shrink-0 shadow-lg">
                    {message.profiles?.avatar_url ? (
                      <img
                        src={message.profiles.avatar_url}
                        alt="Profile"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-500">
                        <Users className="w-5 h-5" />
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[#c9fffc] font-medium">
                      {message.profiles?.display_name || message.profiles?.email}
                    </span>
                    {message.profiles?.is_admin && (
                      <span className="text-xs text-[#c9fffc]/60">Admin</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-400 whitespace-nowrap">
                    {formatTimeShort(message.created_at)}
                  </span>
                  <div className="flex items-center gap-3">
                    <motion.button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isAdmin) {
                          onTogglePin(message.id, message.is_pinned);
                        }
                      }}
                      className={`p-1.5 rounded-lg backdrop-blur-sm transition-all duration-300 bg-[#c9fffc]/20 text-[#c9fffc] shadow-sm shadow-[#c9fffc]/20 ${isAdmin ? 'cursor-pointer' : 'cursor-default opacity-70'}`}
                      title={isAdmin ? 'Unpin message' : 'Only admins can unpin messages'}
                      whileHover={isAdmin ? { scale: 1.05 } : {}}
                      whileTap={isAdmin ? { scale: 0.95 } : {}}
                    >
                      <Pin className="w-4 h-4 fill-current" />
                    </motion.button>
                    {(isAdmin || message.user_id === currentUserId) && (
                      <motion.button
                        onClick={(e) => { e.stopPropagation(); onDelete(message.id, message.user_id); }}
                        className="p-1.5 rounded-lg backdrop-blur-sm transition-all duration-300 bg-red-500/20 text-red-400 shadow-sm"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </motion.button>
                    )}
                  </div>
                </div>
              </div>
              <div className="prose prose-invert prose-sm prose-p:text-white/95 prose-a:text-[#c9fffc] prose-code:text-[#c9fffc] prose-code:bg-[#c9fffc]/10 prose-code:px-1 prose-code:py-0.5 prose-code:rounded break-all overflow-wrap-anywhere mb-3 mt-3" onClick={(e) => e.stopPropagation()}>
                <ReactMarkdown
                  components={{
                    a: ({node, ...props}) => <a onClick={(e) => e.stopPropagation()} {...props} />
                  }}
                >
                  {getFilteredContent(message.content, videoUrls[message.id])}
                </ReactMarkdown>
              </div>
              {message.image_url && (
                <>
                  <button
                    onClick={(e) => { e.stopPropagation(); setOpenImageFor(message.id); }}
                    className="mt-2 relative inline-block rounded-lg overflow-hidden border border-[#c9fffc]/10 group"
                    title="View image"
                  >
                    <img 
                      src={message.image_url} 
                      alt="Message attachment" 
                      className="w-28 h-28 object-cover"
                    />
                    <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button>

                  {openImageFor === message.id && (
                    <Portal>
                      <div
                        className="fixed inset-0 z-[10000] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
                        role="dialog"
                        aria-modal="true"
                        onClick={() => setOpenImageFor(null)}
                      >
                        <div className="relative" onClick={(e) => e.stopPropagation()}>
                          <img
                            src={message.image_url}
                            alt="Message attachment full"
                            className={`object-contain rounded-lg border border-white/10 ${modalSizeClass}`}
                            onLoad={(e) => setImgDims(prev => ({
                              ...prev,
                              [message.id]: { w: (e.target as HTMLImageElement).naturalWidth, h: (e.target as HTMLImageElement).naturalHeight }
                            }))}
                          />
                          <button
                            onClick={() => setOpenImageFor(null)}
                            className="absolute -top-3 -right-3 p-1.5 rounded-full bg-black/70 text-white hover:bg-black/90 border border-white/10"
                            aria-label="Close image"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </Portal>
                  )}
                </>
              )}
              
              {/* Render embedded video player if a video URL is detected */}
              {videoUrls[message.id] && (
                <div onClick={(e) => e.stopPropagation()}>
                  <VideoEmbed url={videoUrls[message.id] || ''} />
                </div>
              )}

              {/* Bottom-right participants avatar stack (show up to 3, then +1) */}
              {(() => {
                const commenters = (message.comments || [])
                  .map((c) => c.profiles)
                  .filter((p) => !!p && p.email !== message.profiles?.email);
                const uniqueMap = new Map<string, typeof commenters[number]>();
                commenters.forEach((p) => {
                  if (!uniqueMap.has(p.email)) uniqueMap.set(p.email, p);
                });
                const unique = Array.from(uniqueMap.values());
                const avatars = unique.filter((p) => p.avatar_url).slice(0, 3);
                const hasExtra = unique.length > avatars.length;
                if (unique.length === 0) return null;
                return (
                  <div className="mt-3 w-full flex justify-end">
                    <div className="flex items-center -space-x-2" title="Participants">
                      {avatars.map((p, idx) => (
                        <img
                          key={p.email || idx}
                          src={p.avatar_url as string}
                          alt={p.display_name || p.email}
                          className="w-6 h-6 rounded-full border border-[#0B1220] bg-gray-700 object-cover"
                          onClick={(e) => e.stopPropagation()}
                        />
                      ))}
                      {hasExtra && (
                        <div className="w-6 h-6 rounded-full bg-[#151D29] border border-[#0B1220] text-[10px] text-gray-300 flex items-center justify-center select-none">
                          +1
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  );
}